import { useMemo } from "react"
import {
	IconCheckOutline18 as Check,
	IconCircleDashedOutline18 as CircleDashed,
	IconNote2Outline18 as Note,
} from "nucleo-ui-outline-18"
import { useToggle } from "react-use"

import { Button, Loader, Popover } from "@productlane/ui"
import {
	Command,
	CommandEmpty,
	CommandGroup,
	CommandInput,
	CommandItem,
	CommandList,
} from "@productlane/ui/src/components/internal/command"

import type { RouterOutputs } from "@/lib/api"
import { getProjectIcon } from "@/lib/projects"

export type LinearTemplate = NonNullable<
	RouterOutputs["linear"]["templates"]
>[number]

const WORKSPACE_GROUP = "__workspace__"

const TemplateIcon = ({
	template,
	className,
}: {
	template: LinearTemplate
	className?: string
}) => {
	if (!template.icon) return <Note className={className} />
	const Icon = getProjectIcon(template.icon)
	return (
		<Icon
			className={className}
			style={template.color ? { color: template.color } : undefined}
		/>
	)
}

interface Props {
	templates: LinearTemplate[] | null | undefined
	teamNameById: Record<string, string>
	selectedTemplateId?: string
	onSelect: (template: LinearTemplate | null) => void
}

export const TemplateSelect = ({
	templates,
	teamNameById,
	selectedTemplateId,
	onSelect,
}: Props) => {
	const [open, onOpenChange] = useToggle(false)

	const groups = useMemo(() => {
		const byGroup = new Map<string, LinearTemplate[]>()
		for (const template of templates ?? []) {
			const key = template.teamId ?? WORKSPACE_GROUP
			const list = byGroup.get(key) ?? []
			list.push(template)
			byGroup.set(key, list)
		}
		const teamKeys = [...byGroup.keys()]
			.filter((key) => key !== WORKSPACE_GROUP)
			.sort((a, b) =>
				(teamNameById[a] ?? "").localeCompare(teamNameById[b] ?? ""),
			)
		const orderedKeys = byGroup.has(WORKSPACE_GROUP)
			? [...teamKeys, WORKSPACE_GROUP]
			: teamKeys
		return orderedKeys.map((key) => ({
			key,
			heading:
				key === WORKSPACE_GROUP ? "Workspace" : (teamNameById[key] ?? "Team"),
			items: byGroup.get(key) ?? [],
		}))
	}, [templates, teamNameById])

	const selected = templates?.find(({ id }) => id === selectedTemplateId)

	return (
		<Popover
			open={open}
			onOpenChange={onOpenChange}
			align="end"
			className="w-72 p-0"
			content={
				templates === undefined ? (
					<div className="flex items-center justify-center py-6">
						<Loader />
					</div>
				) : (
					<Command>
						<CommandInput
							autoFocus
							showIcon={false}
							placeholder="Apply template..."
						/>
						<CommandList>
							<CommandEmpty>No templates found</CommandEmpty>
							<CommandGroup>
								<CommandItem
									value="no-template"
									keywords={["none", "no template"]}
									onSelect={() => {
										onSelect(null)
										onOpenChange(false)
									}}
								>
									<div className="flex w-full items-center gap-2">
										<CircleDashed className="h-4 w-4 shrink-0" />
										<span className="flex-1 truncate">No template</span>
										{!selectedTemplateId && (
											<Check className="h-4 w-4 shrink-0" />
										)}
									</div>
								</CommandItem>
							</CommandGroup>
							{groups.map((group) => (
								<CommandGroup key={group.key} heading={group.heading}>
									{group.items.map((template) => (
										<CommandItem
											key={template.id}
											value={template.id}
											keywords={[template.name]}
											onSelect={() => {
												onSelect(template)
												onOpenChange(false)
											}}
										>
											<div className="flex w-full items-center gap-2">
												<TemplateIcon
													template={template}
													className="h-4 w-4 shrink-0 text-center leading-4"
												/>
												<span className="flex-1 truncate">{template.name}</span>
												{selectedTemplateId === template.id && (
													<Check className="h-4 w-4 shrink-0" />
												)}
											</div>
										</CommandItem>
									))}
								</CommandGroup>
							))}
						</CommandList>
					</Command>
				)
			}
		>
			<Button
				variant="outline"
				Icon={Note}
				className="hover:bg-gray-150 dark:bg-gray-800 dark:hover:bg-gray-750"
			>
				{selected?.name ?? "Template"}
			</Button>
		</Popover>
	)
}
